import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import type { CrmRecord } from '../../lib/api-types';
import { favoritesQuery, keys, recordQuery, schemaQuery, useSetFavorite } from '../../lib/queries';
import { markdownToPlainText } from '../content/MarkdownContent';
import { Icon } from '../ui/Icon';
import { NoteEditor } from './NoteEditor';
import { NoteSidebar } from './NoteSidebar';
import styles from './Notes.module.css';

export const isNoteObject = (name: string) => name === 'note' || name === 'notes';
const noteText = (record: CrmRecord, field: string) => String(record.data[field] ?? record.fields.find(item => item.name === field)?.value ?? '');
const dateLabel = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function NotesIndex({ objectName }: { objectName: string }) {
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const schema = useQuery(schemaQuery());
  const navigate = useNavigate();
  const cache = useQueryClient();
  const object = schema.data?.objects.find(item => item.name === objectName || item.namePlural === objectName);
  const notes = useInfiniteQuery({
    queryKey: ['records', schema.data?.schemaVersion ?? 0, objectName, 'notebook'],
    // The list endpoint defaults to most recently edited, before pagination.
    queryFn: ({ pageParam }) => api.listRecords(objectName, { limit: 100, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((count, page) => count + page.records.length, 0);
      return last.records.length && loaded < last.totalCount ? loaded : undefined;
    },
    enabled: Boolean(object),
  });
  const total = notes.data?.pages[0]?.totalCount;
  useEffect(() => {
    if (total === undefined || !object) return;
    cache.setQueryData(keys.schema, (current: typeof schema.data) => current && ({ ...current,
      objects: current.objects.map(item => item.id === object.id ? { ...item, recordCount: total } : item) }));
  }, [total, object?.id, cache]);

  if (schema.isLoading) return <p className={styles.state}>Loading notes…</p>;
  if (schema.isError || !object) return <p className={styles.state} role="alert">Notes could not be loaded. <button onClick={() => void schema.refetch()}>Try again</button></p>;
  if (creating) return <NoteDocument objectName={object.namePlural} onClose={() => setCreating(false)}
    onCreated={id => { setCreating(false); void navigate({ to: '/app/o/$object/$recordId', params: { object: object.namePlural, recordId: id } }); }} />;

  const all = [...new Map((notes.data?.pages.flatMap(page => page.records) ?? []).map(record => [record.id, record])).values()];
  const query = search.trim().toLocaleLowerCase();
  const visible = all.filter(record => !query || `${noteText(record, 'title')} ${noteText(record, 'body')}`.toLocaleLowerCase().includes(query));
  return <section className={styles.notebook} aria-label="Notes">
    <header className={styles.indexHeader}>
      <div><h1>Notes <span>{total ?? object.recordCount}</span></h1><p>A little space for your thoughts.</p></div>
      <button type="button" className={styles.primary} onClick={() => setCreating(true)}><Icon name="plus" size={18} /> New Note</button>
    </header>
    <label className={styles.search}><Icon name="search" size={18} /><input type="search" aria-label="Search notes" placeholder="Search notes…" value={search} onChange={event => setSearch(event.target.value)} /></label>
    {notes.isLoading ? <p className={styles.state}>Loading notes…</p> : notes.isError ? <p className={styles.state} role="alert">Couldn’t load notes. <button onClick={() => void notes.refetch()}>Try again</button></p>
      : all.length === 0 ? <div className={styles.empty}><Icon name="notes" size={38} /><h2>Room for a new idea</h2><p>Keep a thought, write a script, or make a list.</p><button type="button" className={styles.primary} onClick={() => setCreating(true)}>Write your first note</button></div>
      : visible.length === 0 ? <p className={styles.state}>No matching notes{notes.hasNextPage ? ' in the notes loaded so far' : ''}.</p>
      : <div className={styles.noteGrid}>{visible.map(record => <Link key={record.id} to="/app/o/$object/$recordId" params={{ object: object.namePlural, recordId: record.id }} className={styles.noteCard}>
        <h2>{noteText(record, 'title') || record.displayName || 'Untitled note'}</h2>
        <p>{markdownToPlainText(noteText(record, 'body')) || 'No additional text'}</p>
        <time dateTime={record.updatedAt}>{dateLabel(record.updatedAt)}</time>
      </Link>)}</div>}
    {notes.hasNextPage && <button type="button" className={styles.loadMore} disabled={notes.isFetchingNextPage} onClick={() => void notes.fetchNextPage()}>{notes.isFetchingNextPage ? 'Loading…' : 'Load more notes'}</button>}
  </section>;
}

export function NotePage({ objectName, recordId }: { objectName: string; recordId: string }) {
  const schema = useQuery(schemaQuery());
  const detail = useQuery({ ...recordQuery(schema.data?.schemaVersion ?? 0, objectName, recordId), enabled: Boolean(schema.data) });
  if (schema.isLoading || detail.isLoading) return <p className={styles.state}>Loading note…</p>;
  if (schema.isError || detail.isError) return <p className={styles.state} role="alert">This note could not be loaded. <button onClick={() => { void schema.refetch(); void detail.refetch(); }}>Try again</button></p>;
  if (!detail.data) return null;
  return <NoteDocument key={recordId} objectName={detail.data.object.namePlural} record={detail.data.record} />;
}

type Snapshot = { title: string; body: string };

function NoteDocument({ objectName, record, onClose, onCreated }: {
  objectName: string; record?: CrmRecord; onClose?: () => void; onCreated?: (id: string) => void;
}) {
  const cache = useQueryClient();
  const initial = () => ({ title: record ? noteText(record, 'title') : '', body: record ? noteText(record, 'body') : '' });
  const [draft, setDraft] = useState<Snapshot>(initial);
  const latestDraft = useRef(draft);
  latestDraft.current = draft;
  const [saved, setSaved] = useState<Snapshot>(initial);
  const baseline = useRef(record?.updatedAt);
  const lastIncoming = useRef(record?.updatedAt);
  const creatingComplete = useRef(false);
  const saveLock = useRef(false);
  const [toolbarTarget, setToolbarTarget] = useState<HTMLDivElement | null>(null);
  const titleInput = useRef<HTMLTextAreaElement>(null);
  const writingSurface = useRef<HTMLDivElement>(null);
  const dirty = draft.title !== saved.title || draft.body !== saved.body;
  const favorites = useQuery({ ...favoritesQuery(), enabled: Boolean(record) });
  const favorite = useSetFavorite();
  const isFavorite = favorites.data?.favorites.some(item => item.recordId === record?.id) ?? false;
  useLayoutEffect(() => {
    const input = titleInput.current;
    if (!input) return;
    const fitTitle = () => { input.style.height = '0px'; input.style.height = `${input.scrollHeight}px`; };
    let previousWidth = input.clientWidth;
    fitTitle();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined && width !== previousWidth) { previousWidth = width; fitTitle(); }
    });
    const parent = input.parentElement;
    if (parent) observer?.observe(parent);
    window.addEventListener('resize', fitTitle);
    return () => { observer?.disconnect(); window.removeEventListener('resize', fitTitle); };
  }, [draft.title]);
  const save = useMutation({
    mutationFn: async (snapshot: Snapshot) => {
      if (saveLock.current) throw new Error('A save is already in progress.');
      saveLock.current = true;
      try {
        const data = { title: snapshot.title.trim() || 'Untitled note', body: snapshot.body };
        if (!record) return await api.createRecord(objectName, data);
        const fresh = await api.getRecord(objectName, record.id);
        if (fresh.record.updatedAt !== baseline.current) throw new Error('This note changed elsewhere. Your writing is still here. Copy your changes before reloading the note to compare.');
        return await api.updateRecord(objectName, record.id, data);
      } finally { saveLock.current = false; }
    },
    onSuccess: (result, snapshot) => {
      baseline.current = result.record.updatedAt;
      setSaved(snapshot);
      if (!record) { creatingComplete.current = true; onCreated?.(result.record.id); }
      for (const key of ['record', 'records', 'favorites', 'timeline', 'search', ...(record ? [] : ['schema', 'bootstrap'])]) {
        void cache.invalidateQueries({ queryKey: [key] });
      }
      void cache.invalidateQueries({ queryKey: keys.recordContext(result.record.id) });
    },
  });

  useBlocker({ disabled: !dirty && !save.isPending,
    enableBeforeUnload: () => (dirty || saveLock.current) && !creatingComplete.current,
    shouldBlockFn: async () => {
      if (creatingComplete.current) return false;
      if (saveLock.current) return true;
      if (!dirty) return false;
      if (record) {
        try {
          await save.mutateAsync(draft);
          // Keep the editor open if more writing arrived while navigation saved.
          return latestDraft.current.title !== draft.title || latestDraft.current.body !== draft.body;
        }
        catch { return !window.confirm('Your note could not be saved. Leave without saving your changes?'); }
      }
      return !window.confirm('Discard this new note?');
    },
  });

  // A clean editor follows Echo/server changes. A dirty editor keeps its draft.
  useEffect(() => {
    if (!record || record.updatedAt === lastIncoming.current) return;
    lastIncoming.current = record.updatedAt;
    if (!record || record.updatedAt === baseline.current || dirty || save.isPending) return;
    baseline.current = record.updatedAt;
    const next = { title: noteText(record, 'title'), body: noteText(record, 'body') };
    setDraft(next); setSaved(next);
  }, [record, dirty, save.isPending]);

  useEffect(() => {
    if (!record || !dirty || save.isPending || save.isError) return;
    const timer = window.setTimeout(() => save.mutate(draft), 1000);
    return () => window.clearTimeout(timer);
  }, [record?.id, draft, dirty, save.isPending, save.isError, save.mutate]);

  const close = () => { if (!save.isPending && (!dirty || window.confirm('Discard this new note?'))) onClose?.(); };
  return <section className={styles.document} aria-label={record ? 'Note' : 'New note'}
    onKeyDown={event => {
      if ((event.metaKey || event.ctrlKey) && (event.key.toLowerCase() === 's' || event.key === 'Enter')) {
        event.preventDefault(); if (!save.isPending && dirty) save.mutate(draft);
      }
    }}>
    <header className={styles.documentHeader}>
      {onClose ? <button type="button" className={styles.back} onClick={close} disabled={save.isPending}><Icon name="chevron-left" size={18} /> Notes</button>
        : <Link className={styles.back} to="/app/o/$object" params={{ object: objectName }}><Icon name="chevron-left" size={18} /> Notes</Link>}
      <div ref={setToolbarTarget} className={styles.headerFormatting} />
      <span className={styles.saveStatus} role="status">{save.isPending ? 'Saving…' : save.isError ? 'Not saved' : dirty ? 'Unsaved changes' : record ? 'All changes saved' : 'New note'}</span>
      {record && <button type="button" className={styles.iconButton} aria-label={isFavorite ? 'Remove favorite' : 'Add to favorites'} aria-pressed={isFavorite}
        disabled={!favorites.data || favorites.isError || favorite.isPending} onClick={() => favorite.mutate({ recordId: record.id, favorite: !isFavorite })}><Icon name="star" size={19} /></button>}
      <button type="button" className={record ? styles.quietButton : styles.primary} disabled={save.isPending || (Boolean(record) && !dirty)}
        onClick={() => save.mutate(draft)}>{save.isPending ? 'Saving…' : record ? 'Save' : 'Create note'}</button>
    </header>
    {save.error && <div className={styles.error} role="alert">{save.error.message} <button type="button" disabled={save.isPending} onClick={() => save.mutate(draft)}>Try saving again</button></div>}
    {favorite.isError && <p className={styles.error} role="alert">Couldn’t update favorites. Try again.</p>}
    <div className={styles.documentWorkspace}>
      <div className={styles.documentScroll}>
        <div className={styles.paper} ref={writingSurface}>
          <textarea ref={titleInput} rows={1} className={styles.noteTitle} aria-label="Note title" placeholder="Title" value={draft.title} autoFocus={!record} disabled={!record && save.isPending}
            onChange={event => setDraft(current => ({ ...current, title: event.target.value }))}
            onKeyDown={event => {
              if ((event.key === 'Enter' || (event.key === 'ArrowDown' && event.currentTarget.selectionStart === event.currentTarget.value.length)) && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault(); writingSurface.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
              }
            }} />
          <NoteEditor value={draft.body} onChange={body => setDraft(current => ({ ...current, body }))} disabled={!record && save.isPending}
            toolbarTarget={toolbarTarget} onNavigateToTitle={() => {
              titleInput.current?.focus(); titleInput.current?.setSelectionRange(draft.title.length, draft.title.length);
            }} />
        </div>
      </div>
      <NoteSidebar record={record} objectName={objectName} canEdit={!dirty && !save.isPending} />
    </div>
  </section>;
}
